import { ReturnIcon } from 'components/icons'
import { useSelect } from 'context/select-context'
import classNames from 'helpers/class-names'
import React, { FC, SVGProps } from 'react'
import { useListItem } from '@floating-ui/react'

interface FancyComboboxItemProps {
  label: string
  icon?: FC<SVGProps<SVGSVGElement>>
  marker?: 'green' | 'gray' | 'light-gray'
  onSelect: () => void
}

export const FancyComboboxItem: FC<FancyComboboxItemProps> = ({
  label,
  icon: Icon,
  marker,
  onSelect,
}) => {
  const { activeIndex, getItemProps, handleSelect } = useSelect()
  const { ref, index } = useListItem({ label })
  const isActive = activeIndex === index

  const itemClasses = (active: boolean): string =>
    classNames(
      'group flex w-full items-center px-2 py-1 text-left gap-1 font-semibold text-gray-400',
      'text-sm outline-none',
      { 'bg-gray-50 text-gray-500': active },
    )

  return (
    <button
      ref={ref}
      tabIndex={isActive ? 0 : -1}
      className={classNames(itemClasses(isActive), 'font-body group')}
      {...getItemProps({
        onClick: (event: React.MouseEvent) => {
          event.stopPropagation()
          onSelect()
          handleSelect(index)
        },
      })}>
      {Icon ? (
        <span className="flex-shrink-0">
          <Icon className="w-4 text-gray-300" />
        </span>
      ) : (
        <span />
      )}
      <span className="flex-1 truncate">{label}</span>
      {isActive && (
        <ReturnIcon className="size-4 text-gray-300 group-focus-visible:block hidden" />
      )}
      {marker && (
        <div
          className={classNames(
            'h-2 w-2 flex-shrink-0 rounded-full bg-gray-300',
            {
              'bg-success': marker === 'green',
              'bg-gray-300': marker === 'gray',
              'bg-gray-100': marker === 'light-gray',
            },
          )}
        />
      )}
    </button>
  )
}
